import {
  addMonths,
  addQuarters,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import type { BalanceSheetSettings } from "../../../../../../api/types/svTypes.ts";
import { ShowPriorPeriods } from "../../../../../../api/types/svTypes.ts";

export type BalanceSheetPeriodPreset =
  | "Today"
  | "ThisMonth"
  | "ThisQuarter"
  | "ThisYear"
  | "LastMonth"
  | "LastQuarter"
  | "LastYear"
  | "CustomRange";

export type BalanceSheetPeriodGranularity = "month" | "quarter" | "year" | "custom";
export type BalanceSheetPeriodShiftDirection = "previous" | "next";
export type BalanceSheetPriorPeriodType = "month" | "quarter" | "year" | "custom";
export type BalanceSheetCountedPriorPeriodType = Exclude<BalanceSheetPriorPeriodType, "custom">;

export interface BalanceSheetPeriodRange {
  from: Date;
  to: Date;
}

export interface BalanceSheetPeriodState {
  preset: BalanceSheetPeriodPreset;
  granularity: BalanceSheetPeriodGranularity;
  anchorDate: Date;
  priorPeriodType: BalanceSheetPriorPeriodType;
  priorPeriodCount: number;
  customRange?: BalanceSheetPeriodRange;
}

export interface BalanceSheetPeriodMenuItem {
  preset: BalanceSheetPeriodPreset;
  label: string;
}

export interface BalanceSheetPresetMenuItem {
  preset: Exclude<BalanceSheetPeriodPreset, "CustomRange">;
  label: string;
}

export const todayBalanceSheetPeriodMenuItem: BalanceSheetPresetMenuItem = {
  preset: "Today",
  label: "Today",
};

export const currentBalanceSheetPeriodMenuItems: readonly BalanceSheetPresetMenuItem[] = [
  { preset: "ThisMonth", label: "This Month" },
  { preset: "ThisQuarter", label: "This Quarter" },
  { preset: "ThisYear", label: "This Year" },
];

export const lastBalanceSheetPeriodMenuItems: readonly BalanceSheetPresetMenuItem[] = [
  { preset: "LastMonth", label: "Last Month" },
  { preset: "LastQuarter", label: "Last Quarter" },
  { preset: "LastYear", label: "Last Year" },
];

export const customBalanceSheetPeriodMenuItem: BalanceSheetPeriodMenuItem = {
  preset: "CustomRange",
  label: "Custom Range",
};

export const allBalanceSheetPeriodPresets: readonly BalanceSheetPeriodPreset[] = [
  ...currentBalanceSheetPeriodMenuItems.map((item) => item.preset),
  ...lastBalanceSheetPeriodMenuItems.map((item) => item.preset),
  customBalanceSheetPeriodMenuItem.preset,
];

export const balanceSheetPriorPeriodOptionsByType: Readonly<Record<BalanceSheetPriorPeriodType, readonly number[]>> = {
  month: [1, 3, 6, 12],
  quarter: [1, 2, 3, 4],
  year: [1, 2, 3, 4],
  custom: [],
};

const priorPeriodTypeUiLabelMap: Readonly<Record<BalanceSheetPriorPeriodType, string>> = {
  month: "Month",
  quarter: "Quarter",
  year: "Year",
  custom: "Custom",
};

const priorPeriodOptionSuffixMap: Readonly<Record<BalanceSheetCountedPriorPeriodType, string>> = {
  month: "M",
  quarter: "Q",
  year: "Y",
};

const toQuarterLabel = (value: Date): string => `Q${getQuarter(value)} ${format(value, "yyyy")}`;

const toPresetAnchorDate = (preset: BalanceSheetPeriodPreset, now: Date): Date => {
  switch (preset) {
    case "Today":
    case "ThisMonth":
    case "ThisQuarter":
    case "ThisYear": {
      return now;
    }
    case "LastMonth": {
      return subMonths(now, 1);
    }
    case "LastQuarter": {
      return subQuarters(now, 1);
    }
    case "LastYear": {
      return subYears(now, 1);
    }
    case "CustomRange": {
      return now;
    }
  }
};

const toGranularity = (preset: BalanceSheetPeriodPreset): BalanceSheetPeriodGranularity => {
  switch (preset) {
    case "Today": {
      return "custom";
    }
    case "ThisMonth":
    case "LastMonth": {
      return "month";
    }
    case "ThisQuarter":
    case "LastQuarter": {
      return "quarter";
    }
    case "ThisYear":
    case "LastYear": {
      return "year";
    }
    case "CustomRange": {
      return "custom";
    }
  }
};

const toValidPriorPeriodCount = (type: BalanceSheetPriorPeriodType, count: number): number => {
  return balanceSheetPriorPeriodOptionsByType[type].includes(count) ? count : 1;
};

export const getBalanceSheetPeriodStateFromPreset = (
  preset: BalanceSheetPeriodPreset,
  now: Date = new Date()
): BalanceSheetPeriodState => {
  const granularity = toGranularity(preset);

  return {
    preset,
    granularity,
    anchorDate: toPresetAnchorDate(preset, now),
    priorPeriodType: granularity,
    priorPeriodCount: 1,
  };
};

export const createDefaultBalanceSheetPeriodState = (now: Date = new Date()): BalanceSheetPeriodState =>
  getBalanceSheetPeriodStateFromPreset("ThisQuarter", now);

export const createCustomBalanceSheetPeriodState = (range: BalanceSheetPeriodRange): BalanceSheetPeriodState => {
  return {
    preset: "CustomRange",
    granularity: "custom",
    anchorDate: range.to,
    priorPeriodType: "custom",
    priorPeriodCount: 1,
    customRange: range,
  };
};

export const applyBalanceSheetPresetSelection = (
  current: BalanceSheetPeriodState,
  preset: Exclude<BalanceSheetPeriodPreset, "CustomRange">,
  now: Date = new Date()
): BalanceSheetPeriodState => {
  const next = getBalanceSheetPeriodStateFromPreset(preset, now);
  const presetPeriodType = next.priorPeriodType;
  const shouldKeepCurrentCount = presetPeriodType === current.priorPeriodType;

  return {
    ...next,
    priorPeriodCount: shouldKeepCurrentCount ? toValidPriorPeriodCount(presetPeriodType, current.priorPeriodCount) : 1,
  };
};

export const getBalanceSheetPriorPeriodTypeUiLabel = (type: BalanceSheetPriorPeriodType): string =>
  priorPeriodTypeUiLabelMap[type];

export const getBalanceSheetPriorPeriodOptionLabel = (
  type: BalanceSheetCountedPriorPeriodType,
  count: number
): string => `${count}${priorPeriodOptionSuffixMap[type]}`;

export const setBalanceSheetPriorPeriodType = (
  current: BalanceSheetPeriodState,
  type: BalanceSheetPriorPeriodType
): BalanceSheetPeriodState => {
  if (current.priorPeriodType === type) {
    return current;
  }

  return {
    ...current,
    priorPeriodType: type,
    priorPeriodCount: 1,
  };
};

export const setBalanceSheetPriorPeriodCount = (
  current: BalanceSheetPeriodState,
  count: number
): BalanceSheetPeriodState => ({
  ...current,
  priorPeriodCount: toValidPriorPeriodCount(current.priorPeriodType, count),
});

export const getBalanceSheetPriorPeriodSettings = (state: BalanceSheetPeriodState): BalanceSheetSettings => {
  return getBalanceSheetPriorPeriodSettingsFromSelection(state.priorPeriodType, state.priorPeriodCount);
};

export const getBalanceSheetPriorPeriodSettingsFromSelection = (
  priorPeriodType: BalanceSheetPriorPeriodType,
  priorPeriodCount: number
): BalanceSheetSettings => {
  if (priorPeriodType === "custom") {
    return { showPriorPeriods: ShowPriorPeriods.None };
  }

  const showPriorPeriodsMap: Record<BalanceSheetCountedPriorPeriodType, ShowPriorPeriods> = {
    month: ShowPriorPeriods.ByMonth,
    quarter: ShowPriorPeriods.ByQuarter,
    year: ShowPriorPeriods.ByYear,
  };

  return {
    showPriorPeriods: showPriorPeriodsMap[priorPeriodType],
    priorPeriodCount: toValidPriorPeriodCount(priorPeriodType, priorPeriodCount),
  };
};

export const getBalanceSheetPeriodRange = (state: BalanceSheetPeriodState): BalanceSheetPeriodRange => {
  if (state.preset === "Today") {
    const today = state.anchorDate;
    return { from: today, to: today };
  }

  if (state.preset === "CustomRange" && state.customRange) {
    return state.customRange;
  }

  switch (state.granularity) {
    case "month": {
      return {
        from: startOfMonth(state.anchorDate),
        to: endOfMonth(state.anchorDate),
      };
    }
    case "quarter": {
      return {
        from: startOfQuarter(state.anchorDate),
        to: endOfQuarter(state.anchorDate),
      };
    }
    case "year": {
      return {
        from: startOfYear(state.anchorDate),
        to: endOfYear(state.anchorDate),
      };
    }
    case "custom": {
      return {
        from: startOfQuarter(state.anchorDate),
        to: endOfQuarter(state.anchorDate),
      };
    }
  }
};

export const getBalanceSheetPeriodDisplayLabel = (state: BalanceSheetPeriodState): string => {
  if (state.preset === "Today") {
    return format(state.anchorDate, "MMM dd, yyyy");
  }

  if (state.preset === "CustomRange" && state.customRange) {
    return `${format(state.customRange.from, "MMM dd, yyyy")} - ${format(state.customRange.to, "MMM dd, yyyy")}`;
  }

  switch (state.granularity) {
    case "month": {
      return format(state.anchorDate, "MMM yyyy");
    }
    case "quarter": {
      return toQuarterLabel(state.anchorDate);
    }
    case "year": {
      return format(state.anchorDate, "yyyy");
    }
    case "custom": {
      return toQuarterLabel(state.anchorDate);
    }
  }
};

export const getBalanceSheetPeriodMenuDescription = (
  preset: BalanceSheetPeriodPreset,
  now: Date = new Date()
): string => {
  switch (preset) {
    case "Today": {
      return format(now, "MMM dd, yyyy");
    }
    case "ThisMonth": {
      return format(now, "MMM yyyy");
    }
    case "ThisQuarter": {
      return toQuarterLabel(now);
    }
    case "ThisYear": {
      return format(now, "yyyy");
    }
    case "LastMonth": {
      return format(subMonths(now, 1), "MMM yyyy");
    }
    case "LastQuarter": {
      return toQuarterLabel(subQuarters(now, 1));
    }
    case "LastYear": {
      return format(subYears(now, 1), "yyyy");
    }
    case "CustomRange": {
      return "";
    }
  }
};

export const isBalanceSheetPeriodShiftEnabled = (state: BalanceSheetPeriodState): boolean =>
  state.preset !== "CustomRange" && state.preset !== "Today";

export const shiftBalanceSheetPeriod = (
  state: BalanceSheetPeriodState,
  direction: BalanceSheetPeriodShiftDirection
): BalanceSheetPeriodState => {
  if (!isBalanceSheetPeriodShiftEnabled(state)) {
    return state;
  }

  const step = direction === "next" ? 1 : -1;
  let anchorDate = state.anchorDate;

  switch (state.granularity) {
    case "month": {
      anchorDate = addMonths(anchorDate, step);
      break;
    }
    case "quarter": {
      anchorDate = addQuarters(anchorDate, step);
      break;
    }
    case "year": {
      anchorDate = addYears(anchorDate, step);
      break;
    }
    case "custom": {
      break;
    }
  }

  return {
    ...state,
    anchorDate,
  };
};
